import { RECEPTIONIST_SALE_PAGES } from '../../pages/receptionist/sales/salePageConfig';
import { formatGymPrice } from '../../utils/gymMoney';
import { pkTodayKey } from '../../utils/pkTime';

const SALE_PAGE_SOURCES = new Set(
  RECEPTIONIST_SALE_PAGES.map((p) => String(p.source || '').toLowerCase()),
);

const EXCLUDED_NEW_MEMBERSHIP_SOURCES = new Set([
  ...SALE_PAGE_SOURCES,
  'guest_pass',
  'guestpass',
  'visitor',
  'remaining_clear',
  'remaining',
  'balance_payment',
  'balance_clear',
  'pay_balance',
  'refund',
  'expense',
]);

export function dashboardTodayKey() {
  return pkTodayKey();
}

export function dashboardMonthStartKey() {
  return `${dashboardTodayKey().slice(0, 7)}-01`;
}

/** Paid amount — header first, then splits (same on Rec + Admin). */
export function dashboardPaymentAmount(p) {
  const header = Number(p?.amount) || 0;
  if (header > 0) return header;
  const splits = Array.isArray(p?.payment_splits)
    ? p.payment_splits
    : (Array.isArray(p?.paymentSplits) ? p.paymentSplits : []);
  return splits.reduce((sum, row) => sum + (Number(row?.amount) || 0), 0);
}

export function dashboardPaymentDay(p) {
  return String(p?.paidAt || p?.paid_at || p?.date || '').slice(0, 10);
}

export function isDashboardRenewalPayment(p) {
  const s = String(p?.source || '').toLowerCase().trim();
  if (s === 'renewal_sale' || s === 'renewal') return true;
  const note = String(p?.paymentNote || p?.payment_note || '');
  return /^(Renewed|Complimentary renewal)/i.test(note.split(' · ')[0] || '');
}

/** New membership / subscription sales only — not renewals, guest, ancillary, remaining clears. */
export function isDashboardNewMembershipPayment(p) {
  if (!p || isDashboardRenewalPayment(p)) return false;
  const s = String(p?.source || '').toLowerCase().trim();
  if (EXCLUDED_NEW_MEMBERSHIP_SOURCES.has(s)) return false;
  return (
    !s
    || s === 'subscription'
    || s === 'membership'
    || s === 'new_membership'
    || s === 'subscriptions'
  );
}

export function isDashboardGuestPassPayment(p) {
  const s = String(p?.source || '').toLowerCase().trim();
  return s === 'guest_pass' || s === 'guestpass' || s === 'visitor';
}

export function inDashboardDateRange(value, startDate, endDate) {
  const d = String(value || '').slice(0, 10);
  if (!d) return false;
  if (startDate && d < startDate) return false;
  if (endDate && d > endDate) return false;
  return true;
}

/**
 * Sale / New Membership window:
 * - with date filter → selected range
 * - without filter → current calendar month (same idea as Total Revenue)
 */
export function dashboardSaleScoped(p, startDate, endDate, filtered) {
  const d = dashboardPaymentDay(p);
  if (filtered) return inDashboardDateRange(d, startDate, endDate);
  const monthStart = dashboardMonthStartKey();
  return Boolean(d) && d >= monthStart;
}

export function dashboardRevenuePayments(payments, startDate, endDate, filtered) {
  if (filtered) {
    return (payments || []).filter((p) => inDashboardDateRange(dashboardPaymentDay(p), startDate, endDate));
  }
  const monthStart = dashboardMonthStartKey();
  return (payments || []).filter((p) => {
    const d = dashboardPaymentDay(p);
    return d && d >= monthStart;
  });
}

/**
 * Shared sale KPI block used by Reception + Admin dashboards.
 * @param {object[]} payments — already branch-scoped (or gym-wide)
 * @param {{ startDate?: string, endDate?: string, filtered?: boolean, scopeHint?: string }} opts
 */
export function computeDashboardSaleStats(payments, opts = {}) {
  const startDate = opts.startDate || '';
  const endDate = opts.endDate || '';
  const filtered = Boolean(opts.filtered);
  const scopeHint = opts.scopeHint || '';
  const list = payments || [];

  const saleScoped = (p) => dashboardSaleScoped(p, startDate, endDate, filtered);

  const newMembershipPays = list.filter((p) => isDashboardNewMembershipPayment(p) && saleScoped(p));
  const newMembershipCount = newMembershipPays.length;
  const newMembershipAmount = newMembershipPays.reduce((sum, p) => sum + dashboardPaymentAmount(p), 0);
  const registrationFeeTotal = newMembershipPays.reduce(
    (sum, p) => sum + (Number(p.registrationFee ?? p.registration_fee) || 0),
    0,
  );

  const renewalPays = list.filter((p) => isDashboardRenewalPayment(p) && saleScoped(p));
  const renewalAmount = renewalPays.reduce((sum, p) => sum + dashboardPaymentAmount(p), 0);

  const saleStats = RECEPTIONIST_SALE_PAGES.map((page) => {
    const rows = list.filter((p) => {
      if (String(p.source || '').toLowerCase() !== page.source) return false;
      return saleScoped(p);
    });
    const amount = rows.reduce((sum, p) => sum + dashboardPaymentAmount(p), 0);
    return {
      key: page.source,
      label: filtered ? `${page.label} (range)` : `${page.label} (month)`,
      value: String(rows.length),
      change: amount > 0 ? formatGymPrice(amount) : 'Rs 0',
      variant: 'success',
    };
  });

  const revenuePayments = dashboardRevenuePayments(list, startDate, endDate, filtered);
  const totalRevenue = revenuePayments.reduce((sum, p) => sum + dashboardPaymentAmount(p), 0);

  const guestPays = list.filter((p) => isDashboardGuestPassPayment(p) && saleScoped(p));
  const guestPassCount = guestPays.length;
  const guestPassAmount = guestPays.reduce((sum, p) => sum + dashboardPaymentAmount(p), 0);

  const rangeSuffix = filtered ? 'range' : 'month';
  const revenueChange = filtered
    ? (scopeHint ? `${scopeHint} · selected dates` : 'Selected dates')
    : (scopeHint ? `${scopeHint} · current calendar month` : 'Current calendar month');
  const windowHint = filtered
    ? (scopeHint ? `${scopeHint} · selected dates` : 'Selected dates')
    : (scopeHint ? `${scopeHint} · current calendar month` : 'Current calendar month');

  return {
    newMembershipCount,
    newMembershipAmount,
    registrationFeeTotal,
    renewalCount: renewalPays.length,
    renewalAmount,
    saleStats,
    totalRevenue,
    revenuePaymentCount: revenuePayments.length,
    guestPassCount,
    guestPassAmount,
    newMembershipStat: {
      key: 'newMembership',
      label: filtered ? 'New Membership (range)' : 'New Membership (month)',
      value: String(newMembershipCount),
      change: formatGymPrice(newMembershipAmount),
      variant: 'success',
    },
    newMembershipAmountStat: {
      key: 'newMembershipAmount',
      label: filtered ? 'New Membership Amount (range)' : 'New Membership Amount (month)',
      value: formatGymPrice(newMembershipAmount),
      change: `${newMembershipCount} sale${newMembershipCount === 1 ? '' : 's'}`,
      variant: 'success',
    },
    registrationFeeStat: {
      key: 'registrationFee',
      label: filtered ? 'Registration Fee (range)' : 'Registration Fee (month)',
      value: formatGymPrice(registrationFeeTotal),
      change: 'From new memberships',
      variant: 'info',
    },
    guestPassStat: {
      key: 'guestPass',
      label: filtered ? 'Guest Pass (range)' : 'Guest Pass (month)',
      value: String(guestPassCount),
      change: guestPassAmount > 0 ? formatGymPrice(guestPassAmount) : 'Rs 0',
      variant: 'info',
    },
    revenueStat: {
      key: 'collection',
      label: filtered ? 'Total Revenue (range)' : 'Total Revenue (month)',
      value: formatGymPrice(totalRevenue),
      change: revenueChange,
      variant: 'success',
    },
    revenueStatAdmin: {
      key: 'revenue',
      label: filtered ? 'Total Revenue (range)' : 'Total Revenue (month)',
      value: formatGymPrice(totalRevenue),
      change: revenueChange,
    },
    paymentsCountStat: {
      key: 'payments',
      label: filtered ? 'Payments (range)' : 'Payments (month)',
      value: String(revenuePayments.length),
      change: totalRevenue ? formatGymPrice(totalRevenue) : '',
    },
    windowHint,
    rangeSuffix,
  };
}
